import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { Opportunity } from "@entities";
import { opportunityRepo } from "@repositories";
import {
  GROUP_VOLUNTEER_REGIONS,
  GROUP_VOLUNTEER_REGION_LABELS,
} from "@shared/helpers/group-volunteer-regions";
import { getIsraeliIdValidationError } from "@shared/helpers/israeli-id";
import {
  flattenSummerCampaignLeadMatches,
  isLeadOpportunityOffPreferences,
  type SummerCampaignLeadMatchItem,
} from "@shared/helpers/summer-campaign-lead-admin";
import { type RelationalOrJsonEntityFilter } from "@shared/helpers/sql-relations";
import {
  OpportunityVolunteerMode,
  allowsGroupVolunteer,
  allowsIndividualVolunteerApp,
} from "@shared/helpers/opportunity-volunteer-mode";
import { linko } from "@shared/linko";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import SelectOpportunitiesTable from "../select-opportunities-table";
import { getFilters } from "../opportunities-table/filters";
import { LeadContextPanel, type LeadContextData } from "./LeadContextPanel";
import {
  LeadMatchedOpportunitiesPanel,
  type LeadMatchRow,
} from "./LeadMatchedOpportunitiesPanel";
import {
  LeadMissionTypeSwitcher,
  resolveIsGroupFilter,
  type MissionAttendanceFilter,
  type MissionBrowseMode,
} from "./LeadMissionTypeSwitcher";
import { LeadActionTooltip } from "./LeadActionTooltip";
import { LEAD_ACTION_TOOLTIPS } from "./lead-actions-copy";
import { OpportunityPreviewDialog } from "../../../components/opportunity-preview/OpportunityPreviewDialog";
import {
  patchLeadAfterProcess,
  refreshLeadHandlingBadges,
} from "../../../queries/invalidate-lead-handling";

type LeadSummary = {
  id: string;
  phoneNumber: string;
  regionLabel: string;
  attendanceLabel: string;
  attendance: string | null;
  teamNames: string[];
  categoryNames: string[];
  contactConsent: boolean;
  teamIds: string[];
  categoryIds: string[];
  availabilityStart?: string | Date | null;
  availabilityEnd?: string | Date | null;
  volunteerHouse?: boolean;
  handlingManagerId?: string | null;
  handlingManagerName?: string | null;
  handlingSince?: string | Date | null;
};

type ProcessDoneResult = {
  status: string;
  registrationCount?: number;
  contactStatus?: string;
};

type Props = {
  open: boolean;
  lead: LeadSummary;
  onClose: () => void;
  onDone: (result?: ProcessDoneResult) => void;
};

const STEPS = ["בחירת התנדבויות", "פרטי מתנדב", "אישור ורישום"];

export const ProcessLeadDialog: FC<Props> = ({ open, lead, onClose, onDone }) => {
  const { currOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [numberID, setNumberID] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupSize, setGroupSize] = useState("5");
  const [groupRegions, setGroupRegions] = useState<string[]>(["CENTER"]);
  const [selectedIds, setSelectedIds] = useState<Record<string, LeadMatchRow>>({});
  const [sendNotification, setSendNotification] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFullScreen, setPickerFullScreen] = useState(false);
  const [browseMode, setBrowseMode] = useState<MissionBrowseMode>("matched");
  const [attendanceFilter, setAttendanceFilter] =
    useState<MissionAttendanceFilter>("lead");
  const [resultSummary, setResultSummary] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validateWarnings, setValidateWarnings] = useState<string[]>([]);
  const [previewOpportunityId, setPreviewOpportunityId] = useState<string | null>(null);
  const [previewView, setPreviewView] = useState<"details" | "group-slots">(
    "details",
  );
  const [processResult, setProcessResult] = useState<ProcessDoneResult | null>(
    null,
  );
  const didAutoSelectMatches = useRef(false);

  const resetForm = useCallback(() => {
    setStep(0);
    setName("");
    setNumberID("");
    setDateOfBirth("");
    setGroupName("");
    setGroupSize("5");
    setGroupRegions(["CENTER"]);
    setSelectedIds({});
    setSendNotification(false);
    setAdminNotes("");
    setBrowseMode("matched");
    setAttendanceFilter("lead");
    setResultSummary(null);
    setValidationError(null);
    setValidateWarnings([]);
    setPreviewOpportunityId(null);
    setPreviewView("details");
    setProcessResult(null);
    didAutoSelectMatches.current = false;
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["summer-campaign-lead-detail", lead.id],
    queryFn: async () => {
      const { data } = await linko.summerCampaignLead.queries.getLeadDetail({
        leadId: lead.id,
      });
      return data;
    },
    enabled: open,
  });

  const { data: matches = [], isLoading: matchesLoading } = useQuery({
    queryKey: ["summer-campaign-lead-matches", lead.id],
    queryFn: async () => {
      const { data } = await linko.summerCampaignLead.queries.getLeadMatches({
        leadId: lead.id,
      });
      return data as SummerCampaignLeadMatchItem[];
    },
    // Load matches as soon as the dialog opens - step 0 needs them.
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!detail) return;
    if (detail.existingUser) {
      setName(detail.existingUser.name ?? "");
      setNumberID(detail.existingUser.numberID ?? "");
      setDateOfBirth(
        detail.existingUser.dateOfBirth
          ? dayjs(detail.existingUser.dateOfBirth).format("YYYY-MM-DD")
          : "",
      );
    }
    setAdminNotes(detail.adminNotes ?? "");
    setSendNotification(false);
    if (detail.existingGroupId) {
      setGroupName(
        (prev) => prev || `קבוצת ${detail.existingUser?.name ?? lead.phoneNumber}`,
      );
    }
  }, [detail, lead.phoneNumber]);

  useEffect(() => {
    if (!open || !matches.length || didAutoSelectMatches.current) return;
    const flat = flattenSummerCampaignLeadMatches(matches);
    setSelectedIds((prev) => {
      const next = { ...prev };
      for (const m of flat) {
        if (!m.alreadyRegistered && !m.offPreferences && !next[m.id]) {
          next[m.id] = { ...m, source: "AUTO" };
        }
      }
      return next;
    });
    didAutoSelectMatches.current = true;
  }, [open, matches]);

  const flatMatches = useMemo(
    () => flattenSummerCampaignLeadMatches(matches),
    [matches],
  );

  const opportunityList = useMemo(() => Object.values(selectedIds), [selectedIds]);

  const isGroupFilter = useMemo(
    () => resolveIsGroupFilter(attendanceFilter, lead.attendance),
    [attendanceFilter, lead.attendance],
  );

  const displayedMatches = useMemo((): SummerCampaignLeadMatchItem[] => {
    const matchesInstance = (m: Pick<LeadMatchRow, "volunteerMode">) => {
      if (isGroupFilter === undefined) return true;
      if (isGroupFilter) return allowsGroupVolunteer(m.volunteerMode);
      return allowsIndividualVolunteerApp(m.volunteerMode);
    };

    return matches
      .map((item) => {
        if (item.kind === "group") {
          const instances = item.instances.filter(matchesInstance);
          if (!instances.length) return null;
          const sorted = [...instances].sort((a, b) => {
            const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
            const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
            return aStart - bStart;
          });
          const primary = sorted[0];
          return {
            ...item,
            instances: sorted,
            startDate: primary.startDate ?? item.startDate,
            endDate: primary.endDate ?? item.endDate,
            location: primary.location ?? item.location,
            offPreferences: sorted.some((i) => i.offPreferences),
          };
        }
        return matchesInstance(item) ? item : null;
      })
      .filter((item): item is SummerCampaignLeadMatchItem => item != null);
  }, [matches, isGroupFilter]);

  const matchingOpportunityIds = useMemo(() => {
    const instanceIds = flattenSummerCampaignLeadMatches(displayedMatches)
      .filter((m) => !m.alreadyRegistered)
      .map((m) => m.id);
    const parentIds = displayedMatches
      .filter((item) => item.kind === "group")
      .map((item) => item.id);
    return [...new Set([...instanceIds, ...parentIds])];
  }, [displayedMatches]);

  const pickerInitialSelection = useMemo(() => {
    const ids = new Set(opportunityList.map((o) => o.id));
    for (const id of matchingOpportunityIds) {
      ids.add(id);
    }
    return [...ids];
  }, [opportunityList, matchingOpportunityIds]);

  const contextLead = useMemo((): LeadContextData => {
    return {
      id: lead.id,
      phoneNumber: lead.phoneNumber,
      regionLabel: lead.regionLabel,
      attendanceLabel: lead.attendanceLabel,
      teamNames: lead.teamNames,
      categoryNames: lead.categoryNames,
      availabilityStart:
        detail?.availabilityStart ?? lead.availabilityStart ?? null,
      availabilityEnd: detail?.availabilityEnd ?? lead.availabilityEnd ?? null,
      volunteerHouse: detail?.volunteerHouse ?? lead.volunteerHouse ?? false,
      contactConsent: lead.contactConsent,
      // Prefer detail once loaded - in-dialog claim/release must refresh the pill.
      handlingManagerId: detail
        ? detail.handlingManagerId
        : lead.handlingManagerId,
      handlingManagerName: detail
        ? (detail.handlingManagerName ?? null)
        : (lead.handlingManagerName ?? null),
      handlingSince: detail
        ? (detail.handlingSince ?? null)
        : (lead.handlingSince ?? null),
    };
  }, [lead, detail]);

  const isGroupLead = lead.attendance === "GROUP";
  // Manual group fields are only required for GROUP leads with group
  // opportunities. Individual leads with group opps omit `group` so the server
  // auto-creates a size-1 group.
  const needsGroupDetails =
    isGroupLead && opportunityList.some((o) => allowsGroupVolunteer(o.volunteerMode));

  const buildPayload = () => {
    const parsedBirth = new Date(dateOfBirth);
    if (Number.isNaN(parsedBirth.getTime())) {
      throw new Error("תאריך לידה לא תקין");
    }
    return {
      leadId: lead.id,
      opportunityIds: opportunityList.map((o) => o.id),
      sendNotification,
      adminNotes,
      user: {
        name,
        numberID,
        dateOfBirth: parsedBirth,
      },
      ...(needsGroupDetails
        ? {
            group: {
              name: groupName.trim(),
              size: Number(groupSize),
              preferredVolunteerRegions:
                groupRegions as (typeof GROUP_VOLUNTEER_REGIONS)[number][],
              teamId: lead.teamIds[0],
            },
          }
        : {}),
    };
  };

  const processMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      const { data: validation } =
        await linko.summerCampaignLead.queries.validateProcessLead(payload);
      setValidateWarnings(validation.warnings);
      const invalid = validation.opportunities.filter((o) => !o.ok);
      if (invalid.length) {
        throw new Error(invalid.map((o) => o.message).filter(Boolean).join("\n"));
      }
      const { data } = await linko.summerCampaignLead.mutations.processLead(payload);
      return data;
    },
    onSuccess: (data) => {
      const lines = data.results.map(
        (r) => `${r.title}: ${r.status}${r.message ? ` (${r.message})` : ""}`,
      );
      setResultSummary(`נרשמו ${data.registered} התנדבויות.\n${lines.join("\n")}`);
      setStep(3);
      setValidationError(null);
      setProcessResult({
        status: data.lead?.status ?? "PROCESSED",
        registrationCount: data.lead?.registrationCount,
        contactStatus: data.lead?.contactStatus,
      });
      // Patch list immediately so pending/mine update before the user closes the dialog.
      patchLeadAfterProcess(queryClient, lead.id, {
        status: data.lead?.status ?? "PROCESSED",
        registrationCount: data.lead?.registrationCount,
        contactStatus: data.lead?.contactStatus,
      });
    },
    onError: (err: Error) => {
      setValidationError(err.message || "שגיאה ברישום");
    },
  });

  const extraWhere = useMemo(() => {
    const filter = resolveIsGroupFilter(attendanceFilter, lead.attendance);
    if (filter === undefined) return {} as RelationalOrJsonEntityFilter<Opportunity>;
    return Opportunity.hyperFilter({
      volunteerMode: {
        $in: filter
          ? [
              OpportunityVolunteerMode.GROUP,
              OpportunityVolunteerMode.BOTH,
            ]
          : [
              OpportunityVolunteerMode.INDIVIDUAL,
              OpportunityVolunteerMode.BOTH,
            ],
      },
    }) as RelationalOrJsonEntityFilter<Opportunity>;
  }, [attendanceFilter, lead.attendance]);

  const toolbarFilters = useMemo(
    () => (currOrganization ? getFilters(currOrganization) : []),
    [currOrganization],
  );

  const toggleOpportunity = (row: LeadMatchRow, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (checked) next[row.id] = row;
      else delete next[row.id];
      return next;
    });
  };

  const matchSearchCriteria = useMemo(() => {
    const start = contextLead.availabilityStart;
    const end = contextLead.availabilityEnd;
    const availabilityLabel =
      start && end
        ? `${dayjs(start).format("DD/MM/YYYY")} - ${dayjs(end).format("DD/MM/YYYY")}`
        : undefined;
    return {
      teamNames: contextLead.teamNames,
      categoryNames: contextLead.categoryNames,
      attendanceLabel: contextLead.attendanceLabel,
      availabilityLabel,
    };
  }, [contextLead]);

  const buildMatchRowFromOpportunity = (
    opp: Awaited<ReturnType<typeof opportunityRepo.find>>[number],
    existingMatch?: LeadMatchRow,
  ): LeadMatchRow => {
    if (existingMatch) {
      return { ...existingMatch, source: existingMatch.source === "AUTO" ? "AUTO" : "MANUAL" };
    }
    const oppTeamIds = opp.teams?.map((t) => t.teamId) ?? [];
    const oppCategoryIds = opp.categories?.map((c) => c.categoryId) ?? [];
    return {
      id: opp.id,
      title: opp.title,
      startDate: opp.startDate ?? null,
      endDate: opp.endDate ?? null,
      location: opp.location ?? null,
      hasTime: opp.hasTime ?? false,
      hasStartTime: opp.hasStartTime ?? true,
      timeDescription: opp.timeDescription ?? null,
      source: "MANUAL",
      alreadyRegistered: false,
      offPreferences: isLeadOpportunityOffPreferences(
        lead.attendance,
        lead.teamIds,
        lead.categoryIds,
        {
          volunteerMode: opp.volunteerMode,
          teamIds: oppTeamIds,
          categoryIds: oppCategoryIds,
        },
      ),
      volunteerMode: opp.volunteerMode,
    };
  };

  const resolvePickerOpportunityIds = async (ids: string[]) => {
    if (!ids.length) return [];
    const matchedChildrenByParent = new Map<string, string[]>();
    for (const item of matches) {
      if (item.kind !== "group") continue;
      matchedChildrenByParent.set(
        item.id,
        item.instances.map((instance) => instance.id),
      );
    }

    const opps = await opportunityRepo.find({
      where: { id: { $in: ids } },
    });
    const parentIds = opps
      .filter((opp) => opp.isOpportunityGroupParent)
      .map((opp) => opp.id);
    if (!parentIds.length) return ids;

    const unresolvedParents = parentIds.filter(
      (parentId) => !matchedChildrenByParent.has(parentId),
    );
    const childIdsByParent = new Map(matchedChildrenByParent);
    if (unresolvedParents.length) {
      const children = await opportunityRepo.find({
        where: {
          parentOpportunityId: { $in: unresolvedParents },
          isDeleted: false,
          isClosed: false,
        },
      });
      for (const child of children) {
        if (!child.parentOpportunityId) continue;
        const list = childIdsByParent.get(child.parentOpportunityId) ?? [];
        list.push(child.id);
        childIdsByParent.set(child.parentOpportunityId, list);
      }
    }

    const resolved: string[] = [];
    for (const id of ids) {
      if (parentIds.includes(id)) {
        resolved.push(...(childIdsByParent.get(id) ?? []));
      } else {
        resolved.push(id);
      }
    }
    return [...new Set(resolved)];
  };

  const handleAddManual = async (ids: string[]) => {
    const resolvedIds = await resolvePickerOpportunityIds(ids);
    const opps = await opportunityRepo.find({
      where: { id: { $in: resolvedIds } },
      include: { teams: true, categories: true },
    });
    setSelectedIds((prev) => {
      const next = { ...prev };
      for (const opp of opps) {
        if (!next[opp.id]) {
          const existingMatch = flatMatches.find((m) => m.id === opp.id);
          next[opp.id] = buildMatchRowFromOpportunity(opp, existingMatch);
        }
      }
      return next;
    });
    setPickerOpen(false);
  };

  const handlePickerSave = async (ids: string[]) => {
    const resolvedIds = await resolvePickerOpportunityIds(ids);
    const opps = await opportunityRepo.find({
      where: { id: { $in: resolvedIds } },
      include: { teams: true, categories: true },
    });
    const oppById = new Map(opps.map((o) => [o.id, o]));
    setSelectedIds((prev) => {
      const next = { ...prev };
      for (const id of resolvedIds) {
        const opp = oppById.get(id);
        if (!opp) continue;
        const existingMatch = flatMatches.find((m) => m.id === id) ?? prev[id];
        next[id] = buildMatchRowFromOpportunity(opp, existingMatch);
      }
      return next;
    });
    setPickerOpen(false);
  };

  const idValidationError = useMemo(
    () => getIsraeliIdValidationError(numberID),
    [numberID],
  );

  const canNextVolunteerStep = useMemo(
    () =>
      !idValidationError &&
      Boolean(name.trim() && dateOfBirth) &&
      (!needsGroupDetails ||
        Boolean(groupName.trim() && Number(groupSize) > 0 && groupRegions.length)),
    [
      idValidationError,
      name,
      dateOfBirth,
      needsGroupDetails,
      groupName,
      groupSize,
      groupRegions.length,
    ],
  );

  const canNextMissionsStep =
    lead.contactConsent && opportunityList.length > 0;

  const selectedIdMap = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(selectedIds).map((id) => [id, true]),
      ) as Record<string, boolean>,
    [selectedIds],
  );

  const manualSelections = opportunityList.filter((o) => o.source === "MANUAL");

  const hasGroupOpportunitySelection =
    !isGroupLead && opportunityList.some((o) => allowsGroupVolunteer(o.volunteerMode));
  const hasIndividualOpportunitySelection =
    isGroupLead && opportunityList.some((o) => !allowsGroupVolunteer(o.volunteerMode));

  const invalidateLeadContext = () => {
    void queryClient.invalidateQueries({
      queryKey: ["summer-campaign-lead-detail", lead.id],
    });
    refreshLeadHandlingBadges(queryClient);
  };

  const renderStepContent = () => {
    if (step === 3 && resultSummary) {
      return (
        <Alert severity="success" sx={{ whiteSpace: "pre-line" }}>
          {resultSummary}
        </Alert>
      );
    }

    if (step === 0) {
      return (
        <Stack gap={2}>
          {!lead.contactConsent && (
            <Alert severity="error">
              אין הסכמה ליצירת קשר - לא ניתן לרשום מתנדב מליד זה
            </Alert>
          )}
          {detail?.existingRegistrations && detail.existingRegistrations.length > 0 && (
            <Alert severity="info">
              רישומים קיימים:{" "}
              {detail.existingRegistrations.map((r) => r.title).join(", ")}
            </Alert>
          )}
          {detail?.existingGroupId && needsGroupDetails && (
            <Alert severity="info">
              קבוצה קיימת מקושרת - רישום חוזר ישתמש באותה קבוצה
            </Alert>
          )}
          {hasGroupOpportunitySelection && (
            <Alert severity="info">
              נבחרו התנדבויות קבוצתיות לליד יחיד - המערכת תיצור קבוצה בגודל 1
              ותרשום דרכה.
            </Alert>
          )}
          {hasIndividualOpportunitySelection && (
            <Alert severity="info">
              הליד נרשם כקבוצה, אך נבחרו התנדבויות פרטניות - הרישום אליהן יהיה
              כיחיד.
            </Alert>
          )}

          <LeadMissionTypeSwitcher
            browseMode={browseMode}
            attendanceFilter={attendanceFilter}
            leadAttendance={lead.attendance}
            onBrowseModeChange={setBrowseMode}
            onAttendanceFilterChange={setAttendanceFilter}
          />

          {browseMode === "matched" ? (
            <LeadMatchedOpportunitiesPanel
              matches={displayedMatches}
              selectedIds={selectedIdMap}
              loading={matchesLoading}
              searchCriteria={matchSearchCriteria}
              onPreview={(id, options) => {
                setPreviewView(options?.isGroupParent ? "group-slots" : "details");
                setPreviewOpportunityId(id);
              }}
              onOpenFullTable={() => {
                setPickerFullScreen(true);
                setPickerOpen(true);
              }}
              onToggle={(id, row, checked) =>
                toggleOpportunity({ ...row, source: "AUTO", id }, checked)
              }
              onSelectAll={() => {
                setSelectedIds((prev) => {
                  const next = { ...prev };
                  for (const m of flattenSummerCampaignLeadMatches(displayedMatches)) {
                    if (!m.alreadyRegistered) {
                      next[m.id] = { ...m, source: "AUTO" };
                    }
                  }
                  return next;
                });
              }}
              onClear={() => {
                setSelectedIds((prev) => {
                  const next = { ...prev };
                  for (const m of flattenSummerCampaignLeadMatches(displayedMatches)) {
                    delete next[m.id];
                  }
                  return next;
                });
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              חפש/י משימות בטבלה המלאה או הוסף/י משימה בודדת.
            </Typography>
          )}

          {manualSelections.length > 0 && (
            <Stack gap={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>
                משימות שנוספו ידנית
              </Typography>
              {manualSelections.map((m) => (
                <FormControlLabel
                  key={m.id}
                  control={
                    <Checkbox
                      checked={!!selectedIds[m.id]}
                      onChange={(_, checked) => toggleOpportunity(m, checked)}
                    />
                  }
                  label={
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body2"
                        onClick={() => {
                          setPreviewView("details");
                          setPreviewOpportunityId(m.id);
                        }}
                        sx={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                        }}
                      >
                        {m.title ?? m.id}
                      </Typography>
                      <Chip size="small" label="ידני" color="secondary" />
                      {m.offPreferences && (
                        <Chip size="small" color="warning" label="חורג מהעדפות" />
                      )}
                    </Stack>
                  }
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" gap={1} flexWrap="wrap">
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.addOpportunity}>
              <Button
                variant="outlined"
                onClick={() => {
                  setPickerFullScreen(false);
                  setPickerOpen(true);
                }}
              >
                הוסף התנדבות
              </Button>
            </LeadActionTooltip>
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.fullTable}>
              <Button
                variant="contained"
                onClick={() => {
                  setPickerFullScreen(true);
                  setPickerOpen(true);
                }}
              >
                טבלה מלאה
              </Button>
            </LeadActionTooltip>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            נבחרו {opportunityList.length} התנדבויות
          </Typography>
        </Stack>
      );
    }

    if (step === 1) {
      return (
        <Stack gap={2}>
          <TextField
            label="שם מלא"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="ת.ז"
            value={numberID}
            onChange={(e) => setNumberID(e.target.value)}
            error={!!idValidationError}
            helperText={idValidationError}
            fullWidth
            required
          />
          <TextField
            label="תאריך לידה"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          {needsGroupDetails && (
            <>
              <TextField
                label="שם קבוצה"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="גודל קבוצה"
                type="number"
                value={groupSize}
                onChange={(e) => setGroupSize(e.target.value)}
                fullWidth
                required
              />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {GROUP_VOLUNTEER_REGIONS.map((region) => (
                  <FormControlLabel
                    key={region}
                    control={
                      <Checkbox
                        checked={groupRegions.includes(region)}
                        onChange={(_, checked) =>
                          setGroupRegions((prev) =>
                            checked
                              ? [...prev, region]
                              : prev.filter((r) => r !== region),
                          )
                        }
                      />
                    }
                    label={GROUP_VOLUNTEER_REGION_LABELS[region]}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      );
    }

    if (step === 2) {
      return (
        <Stack gap={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            אישור לפני רישום
          </Typography>
          <Typography variant="body2">
            מתנדב: <strong>{name}</strong>
            {needsGroupDetails ? ` · קבוצה: ${groupName} (${groupSize})` : ""}
          </Typography>
          <Stack gap={0.5}>
            <Typography variant="body2" fontWeight={500}>
              משימות שנבחרו ({opportunityList.length}):
            </Typography>
            {opportunityList.map((o) => (
              <Typography key={o.id} variant="body2" color="text.secondary">
                · {o.title ?? o.id}
                {o.offPreferences ? " (חורג מהעדפות)" : ""}
              </Typography>
            ))}
          </Stack>
          <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.sendNotification}>
            <span style={{ display: "inline-flex" }}>
              <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  disabled={!lead.contactConsent}
                  onChange={(_, v) => setSendNotification(v)}
                />
              }
              label={
                lead.contactConsent
                  ? "שלח התראה למתנדב"
                  : "אין הסכמה ליצירת קשר - לא ניתן לשלוח התראה"
              }
            />
            </span>
          </LeadActionTooltip>
          <TextField
            label="הערות מנהל"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          {validateWarnings.length > 0 && (
            <Alert severity="warning">{validateWarnings.join("\n")}</Alert>
          )}
          {validationError && <Alert severity="error">{validationError}</Alert>}
        </Stack>
      );
    }

    return null;
  };

  const handleDialogClose = () => {
    if (step === 3) {
      onDone(processResult ?? undefined);
      return;
    }
    onClose();
  };

  return (
    <>
      <Dialog open={open && !pickerOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>רישום מתנדב מליד - {lead.phoneNumber}</DialogTitle>
        <DialogContent sx={{ minHeight: "min(70vh, 720px)" }}>
          <Stack gap={2} pt={1}>
            {step < 3 && (
              <Stepper activeStep={step} alternativeLabel sx={{ mb: 1 }}>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            <Stack direction="column" gap={2} alignItems="stretch">
              {step < 3 && (
                <LeadContextPanel
                  lead={contextLead}
                  compact
                  onHandlingChanged={invalidateLeadContext}
                />
              )}
              <Box width="100%" minWidth={0}>
                {step === 1 && detailLoading ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  renderStepContent()
                )}
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <LeadActionTooltip
            title={
              step === 3
                ? LEAD_ACTION_TOOLTIPS.wizardDone
                : LEAD_ACTION_TOOLTIPS.wizardCancel
            }
          >
            <Button onClick={handleDialogClose}>
              {step === 3 ? "סגור" : "ביטול"}
            </Button>
          </LeadActionTooltip>
          {step > 0 && step < 3 && (
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.wizardBack}>
              <Button onClick={() => setStep((s) => s - 1)}>חזרה</Button>
            </LeadActionTooltip>
          )}
          {step === 3 ? (
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.wizardDone}>
              <Button variant="contained" onClick={() => onDone(processResult ?? undefined)}>
                סיום
              </Button>
            </LeadActionTooltip>
          ) : step === 0 ? (
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.wizardNextMissions}>
              <Button
                variant="contained"
                disabled={!canNextMissionsStep}
                onClick={() => setStep(1)}
              >
                המשך לפרטי מתנדב
              </Button>
            </LeadActionTooltip>
          ) : step === 1 ? (
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.wizardNextVolunteer}>
              <Button
                variant="contained"
                disabled={!canNextVolunteerStep}
                onClick={() => setStep(2)}
              >
                המשך לאישור
              </Button>
            </LeadActionTooltip>
          ) : (
            <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.wizardSubmit}>
              <Button
                variant="contained"
                disabled={!canNextVolunteerStep || processMutation.isPending}
                onClick={() => processMutation.mutate()}
              >
                {processMutation.isPending ? "שומר..." : "אשר ורשום"}
              </Button>
            </LeadActionTooltip>
          )}
        </DialogActions>
      </Dialog>

      {pickerOpen ? (
        <SelectOpportunitiesTable
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSave={(ids) =>
            void (pickerFullScreen ? handlePickerSave(ids) : handleAddManual(ids))
          }
          closeOnSave
          openOnly
          fullScreen={pickerFullScreen}
          dismissLabel="חזרה לרישום הליד"
          dismissTooltip={LEAD_ACTION_TOOLTIPS.pickerBackToLead}
          showRowScopeSwitch
          toolbarFilters={toolbarFilters}
          extraWhere={extraWhere}
          matchingOpportunityIds={matchingOpportunityIds}
          initialSelectedOpportunities={pickerInitialSelection}
          previewOnRowClick
          disableTitleNavigation
          initialRowScope="all"
        />
      ) : null}

      <OpportunityPreviewDialog
        open={!!previewOpportunityId}
        opportunityId={previewOpportunityId}
        view={previewView}
        onClose={() => {
          setPreviewOpportunityId(null);
          setPreviewView("details");
        }}
        nestedInDialog
      />
    </>
  );
};
